import { gql, Resolver } from '@apollo/client';

import { getAllListPages } from '@trqk/utils';

/**
 * @typedef {GetEntitiesWithUploadedStatementsArgs}
 * @property {string} trqkDirectoryUrl URL for the TrqkDirectory GraphQL API
 */
interface GetEntitiesWithUploadedStatementsArgs {
    trqkDirectoryUrl: string;
}

interface GetEntitiesWithUploadedStatementsResult {
    entitiesWithUploadedStatements: EntityWithUploadedStatements[];
}

/**
 * @typedef {GetEntitiesWithUploadedStatementsContext}
 * @property {GetToken} getToken
 */
interface GetEntitiesWithUploadedStatementsContext {
    getToken: GetToken;
}

/**
 * Get a list containing EntityWithUploadedStatements items. These items will be used in the Entity
 * Manager to show a list of entities for the user with a modified count for the number of
 * "uploaded statements" in the table: rather than listing the length of the `statement_ids` array,
 * this column of the table will list the number of statementViews that have a status_name of
 * 'COMPLETE'.
 *
 * Although this is similar to `getActiveEntities`, it has an important difference.
 * `getActiveEntities` is tightly coupled to the `entitySelectionMachine` and only returns entities
 * with at least one completed statement. `getEntitiesWithUploadedStatements` returns a list of
 * objects that each have a count of competed statements, which can be zero.
 * @param obj
 * @param {GetEntitiesWithUploadedStatementsArgs} args
 * @param context
 * @returns {Promise<GetEntitiesWithUploadedStatementsResult[]>} an array of
 * EntityWithUploadedStatements items.
 */
export const getEntitiesWithUploadedStatements: Resolver = async (
    obj: Record<string, unknown>,
    args: GetEntitiesWithUploadedStatementsArgs,
    context: GetEntitiesWithUploadedStatementsContext,
): Promise<GetEntitiesWithUploadedStatementsResult> => {
    const { getToken } = context;
    const { trqkDirectoryUrl } = args;

    const items = await getAllListPages<
        TrqkEntity | null,
        TableTrqkEntityFilterInput
    >({
        getToken,
        query: `
            query listTrqkEntitiesWithUploadedStatements($filter: TableTrqkEntityFilterInput, $limit: Int, $nextToken: String) {
                listTrqkEntities(filter: $filter, limit: $limit, nextToken: $nextToken) {
                    items {
                        id
                        name
                        pro
                        role_type
                        ROW_LOCK
                    }
                    nextToken
                }
            }
        `,
        queryName: 'listTrqkEntities',
        trqkDirectoryUrl,
    });

    function itemIsNotNull(x: TrqkEntity | null): x is TrqkEntity {
        return x !== null;
    }

    const entities = items.filter(itemIsNotNull);

    const allEntities = await Promise.all(
        entities.map(
            (entity: TrqkEntity): Promise<ActiveEntity> =>
                new Promise((resolve, reject) => {
                    getAllListPages<
                        TrqkStatementView,
                        TableTrqkStatementViewFilterInput
                    >({
                        filter: {
                            entity_id: {
                                eq: entity.id,
                            },
                            status_name: {
                                eq: 'COMPLETE',
                            },
                        },
                        getToken,
                        query: `
                            query listTrqkStatementViewsComplete($filter: TableTrqkStatementViewFilterInput, $limit: Int, $nextToken: String) {
                                listTrqkStatementViews(filter: $filter, limit: $limit, nextToken: $nextToken) {
                                    items {
                                        statement_id
                                        start_date
                                        status_name
                                        end_date
                                        pro
                                        region
                                        user_id
                                        unit
                                    }
                                    nextToken
                                }
                            }
                        `,
                        queryName: 'listTrqkStatementViews',
                        trqkDirectoryUrl,
                    })
                        .then(
                            (
                                completedStatementViews: TrqkStatementView[],
                            ): void => {
                                resolve({
                                    id: entity.id,
                                    entity,
                                    completedStatementViews,
                                });
                            },
                        )
                        .catch((error: Error): void => reject(error));
                }),
        ),
    );

    const entitiesWithUploadedStatements = allEntities.map(
        ({
            entity,
            completedStatementViews,
        }: ActiveEntity): EntityWithUploadedStatements => ({
            ...entity,
            uploadedStatements: completedStatementViews.length,
        }),
    );

    return {
        entitiesWithUploadedStatements,
    };
};

export const GET_ENTITIES_WITH_UPLOADED_STATEMENTS = gql`
    query GetEntitiesWithUploadedStatements($trqkDirectoryUrl: String!) {
        getEntitiesWithUploadedStatements(trqkDirectoryUrl: $trqkDirectoryUrl)
            @client
    }
`;
